/**
 * Version checking utility to detect when a new frontend version is available
 * and prompt users to refresh their browser to get the latest version.
 */

const IS_E2E = import.meta.env.MODE === "test";
const VERSION_STORAGE_KEY = "app_version";
const VERSION_CHECK_TIMESTAMP_KEY = "version_check_timestamp";
const POLLING_INTERVAL_MS = 10 * 60 * 1000; // Polling: check for updates every 10 minutes

/**
 * Gets the current app version from environment variables
 */
export const getCurrentVersion = (): string => {
  return import.meta.env.VITE_APP_VERSION || "0.0.0";
};

/**
 * Gets the stored version from localStorage
 */
const getStoredVersion = (): string | null => {
  try {
    return localStorage.getItem(VERSION_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

/**
 * Stores the current version in localStorage
 */
const storeVersion = (version: string): void => {
  try {
    localStorage.setItem(VERSION_STORAGE_KEY, version);
  } catch (error) {
    console.warn("Failed to store version in localStorage:", error);
  }
};

/**
 * Gets the last version check timestamp
 */
const getLastCheckTimestamp = (): number => {
  try {
    const timestamp = localStorage.getItem(VERSION_CHECK_TIMESTAMP_KEY);

    return timestamp ? parseInt(timestamp, 10) : 0;
  } catch (error) {
    console.warn("Failed to read check timestamp from localStorage:", error);

    return 0;
  }
};

/**
 * Stores the current timestamp as the last check time
 */
const storeCheckTimestamp = (): void => {
  try {
    localStorage.setItem(VERSION_CHECK_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn("Failed to store check timestamp in localStorage:", error);
  }
};

/**
 * Checks if a new version is available by comparing the current version
 * with the stored version in localStorage.
 *
 * @returns true if a new version is detected, false otherwise
 */
export const shouldUpdateVersion = (): boolean => {
  // Do nothing in e2e case - every e2e run will use fresh browser with empty localStorage
  if (IS_E2E) {
    return false;
  }

  const currentVersion = getCurrentVersion();
  const storedVersion = getStoredVersion();

  // If no stored version, this is the first run - store current version
  if (!storedVersion) {
    storeVersion(currentVersion);

    return false;
  }

  // Compare versions
  if (currentVersion !== storedVersion) {
    console.log(
      `New version detected: ${currentVersion} (was: ${storedVersion})`,
    );

    return true;
  }

  return false;
};

/**
 * Updates the stored version to the current version.
 * Should be called after the user acknowledges the version update.
 */
export const updateStoredVersion = (): void => {
  const currentVersion = getCurrentVersion();

  storeVersion(currentVersion);
  storeCheckTimestamp();
};

/**
 * Checks if enough time has passed since the last version check
 */
export const shouldCheckVersion = (): boolean => {
  const lastCheck = getLastCheckTimestamp();
  const now = Date.now();

  return now - lastCheck > POLLING_INTERVAL_MS;
};

/**
 * Reloads the page to get the latest version
 * Uses hard reload to bypass cache
 */
export const reloadForUpdate = (): void => {
  window.location.reload();
};

/**
 * Gets the polling interval for periodic version checks
 */
export const getPollingInterval = (): number => {
  // Do nothing in e2e case
  if (IS_E2E) {
    return Infinity;
  }

  return POLLING_INTERVAL_MS;
};
